import { useState } from "react";
import { API } from "@/lib/api";
import { Sparkles, Loader2 } from "lucide-react";

const MODELS = [
  ["claude-sonnet-4-6", "Claude Sonnet 4.6"],
  ["gpt-5.5", "GPT-5.5"],
  ["gemini-3-flash-preview", "Gemini 3 Flash"],
];

function render(md) {
  return md.split("\n").map((line, i) => {
    if (line.startsWith("## "))
      return (
        <h3 key={i} className="font-display font-bold text-sm uppercase tracking-wide mt-4 mb-1.5 text-[#5b21b6]">
          {line.slice(3)}
        </h3>
      );
    if (line.trim().startsWith("- ") || line.trim().startsWith("* "))
      return (
        <div key={i} className="flex gap-2 text-sm leading-relaxed">
          <span className="text-[#8B5CF6] mt-1">▪</span>
          <span dangerouslySetInnerHTML={{ __html: line.trim().slice(2).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
        </div>
      );
    if (!line.trim()) return <div key={i} className="h-2" />;
    return (
      <p key={i} className="text-sm leading-relaxed"
        dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
    );
  });
}

export default function AiInsight({ scope = "dashboard" }) {
  const [model, setModel] = useState(MODELS[0][0]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setText("");
    try {
      const res = await fetch(`${API}/ai/insight`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("erp_token") || ""}`,
        },
        credentials: "omit",
        body: JSON.stringify({ scope, model }),
      });
      if (!res.ok || !res.body) throw new Error("Gagal memanggil AI");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const j = JSON.parse(line);
            if (j.delta) {
              acc += j.delta;
              setText(acc);
            } else if (j.error) {
              acc += `\n\nGagal menghasilkan analisa: ${j.error}`;
              setText(acc);
            }
          } catch {}
        }
      }
    } catch (e) {
      setText(`Gagal menghasilkan analisa: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="ai-insight-panel"
      className="border border-[#c4b5fd] bg-[#faf8ff] p-5 h-full flex flex-col"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#8B5CF6]" />
          <span className="mono text-[11px] uppercase tracking-widest text-[#5b21b6]">
            Analisa AI
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="ai-model-select"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="h-9 px-2 text-xs border border-[#c4b5fd] bg-white outline-none focus:ring-2 focus:ring-[#8B5CF6]"
          >
            {MODELS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <button
            data-testid="ai-generate-button"
            onClick={run}
            disabled={busy}
            className="h-9 px-3 bg-[#8B5CF6] text-white text-xs uppercase tracking-wide flex items-center gap-2 hover:bg-[#7c3aed] transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Analisa
          </button>
        </div>
      </div>

      <div data-testid="ai-insight-output" className="mt-4 flex-1 overflow-auto max-h-[460px] pr-1">
        {text ? (
          render(text)
        ) : (
          <p className="text-sm text-[#6b7280]">
            Klik <strong>Analisa</strong> untuk mendapatkan ringkasan, temuan
            utama, dan rekomendasi aksi berbasis data dashboard Anda.
          </p>
        )}
      </div>
    </div>
  );
}
